
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import { i18n } from "@/i18n";
import PickupForm from "@/components/orders/PickupForm.vue";
import AssistanceServiceForm from "@/components/orders/AssistanceServiceForm.vue";
import DeliveryForm from "@/components/orders/DeliveryForm.vue";
import SupplyMaterialForm from "@/components/orders/SupplyMaterialForm.vue";

import { Action, Getter } from "vuex-class";
import format from "date-fns/format";
import {
  IAssistanceService,
  ICountryConfig,
  IDeliveryService,
  IFault,
  IOrderConfigCommon,
  IPickUpService,
  IRequestedAmount,
  ISolCustomerCommodityType,
  ISolCustomerCurrency,
  ISolCustomerDenomination,
  ISolCustomerDenominationPack,
  ISolCustomerDenominationQuantity,
  ISolCustomerPack,
  ISolCustomerPackQuantity,
  ISolCustomerSupply,
  ISolCustomerSupplyMaterialService,
  IUserClientData,
  IUserClientSubclientData,
  IUserClientSubclientServicepointData,
  IUserDetailData,
  PickUpService,
  SolCustomerOrderType,
  ICountryConfigResponse,
  extractSolSubmissionResult,
  isSolSubmissionSuccess,
  resolveSolSubmissionMessage,
} from "@/entities";
import {
  ISolCustomerDenominationParams,
  ISolCustomerDenominationResponse,
  ISolCustomerFaultParams,
  ISolCustomerFaultResponse,
  ISolCustomerPackParams,
  ISolCustomerPackResponse,
  ISolCustomerResponse,
  IGetProductsParams,
} from "@/services";
import { BModal } from "bootstrap-vue";
import AsyncComputed from "vue-async-computed-decorator";
import { SweetAlertResult } from "sweetalert2";
import { MODULE, MODULE_SOL_SERVICE } from "@/constans";

@Component({
  name: "new-order-modal",
  components: {
    PModal,
    ValidationObserver,
    ValidationProvider,
    PickupForm,
    AssistanceServiceForm,
    DeliveryForm,
    SupplyMaterialForm,
    Multiselect,
  },
})
export default class NewOrderModal extends Vue {
  @Getter("getCashCountryConfig") cashCountryConfig!: ICountryConfigResponse | undefined;
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getSolCustomerCurrencies") solCustomerCurrencies!: ISolCustomerCurrency[];
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;
  @Action("savePickUpService") savePickUpService!: (
    pickUpService: IPickUpService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("saveAssistanceService") saveAssistanceService!: (
    assistanceService: IAssistanceService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("saveDeliveryService") saveDeliveryService!: (
    deliveryService: IDeliveryService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("saveSupplyMaterialService") saveSupplyMaterialService!: (
    supplyMaterialService: ISolCustomerSupplyMaterialService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("fetchSolCustomerFaults") fetchSolCustomerFaults!: (
    params: ISolCustomerFaultParams
  ) => Promise<ISolCustomerFaultResponse | undefined>;
  @Action("fetchSolCustomerDenominations") fetchSolCustomerDenominations!: (
    params: ISolCustomerDenominationParams
  ) => Promise<ISolCustomerDenominationResponse | undefined>;
  @Action("fetchSolCustomerPacks") fetchSolCustomerPacks!: (
    params: ISolCustomerPackParams
  ) => Promise<ISolCustomerPackResponse | undefined>;

  @Prop({ required: true })
  clientSelected: IUserClientData | undefined;
  @Prop({ required: true })
  subclientSelected: IUserClientSubclientData | undefined;
  @Prop({ required: true })
  servicepointSelected: IUserClientSubclientServicepointData | undefined;
  @Prop({ required: true, default: [] })
  clients: IUserClientData[] | undefined;
  @Prop({ required: true, default: [] })
  subclients: IUserClientSubclientData[] | undefined;
  @Prop({ required: true, default: [] })
  servicepoints: IUserClientSubclientServicepointData[] | undefined;
  @Prop({ required: true, default: [] })
  packs!: ISolCustomerPack[];
  @Prop({ required: false, default: () => [] })
  commodityTypes!: ISolCustomerCommodityType[];

  datePattern = "yyyy-MM-dd'T'00:00:00XXX";
  dateTimePattern = "yyyy-MM-dd'T'HH:mm:ssXXX";
  requestType: { value: SolCustomerOrderType; description: string } | null = null;
  optionsRequestType: { value: SolCustomerOrderType; description: string }[] = [];
  serviceDate: Date = new Date();
  amount: number | null = null;
  comments = "";
  fault: IFault | null = null;
  origin = "MYPROSEGUR";
  currency: ISolCustomerCurrency | null =
    this.solCustomerCurrencies?.find((c) => c.mainCurrency === true) || null;
  denominationQuantities: ISolCustomerDenominationQuantity[] = [];
  packQuantities: ISolCustomerPackQuantity[] = [];
  supplies: ISolCustomerSupply[] = [];
  pickupInitialTime = this.isEconoCash
    ? `${this.pickupConfig?.EconoCash?.DefaultStartTime}:00:00`
    : `${this.pickupConfig?.DefaultStartTime}:00:00`;
  pickupFinalTime = this.isEconoCash
    ? `${this.pickupConfig?.EconoCash?.DefaultFinalTime}:00:00`
    : `${this.pickupConfig?.DefaultFinalTime}:00:00`;
  deliveryInitialTime = `${this.pickupConfig?.DefaultStartTime}:00:00`;
  deliveryFinalTime = `${this.pickupConfig?.DefaultFinalTime}:00:00`;
  assistanceInitialTime = `${this.assistanceConfig?.DefaultStartTime}:00:00`;
  assistanceFinalTime = `${this.assistanceConfig?.DefaultFinalTime}:00:00`;

  constructor() {
    super();
  }

  get enabledSolServicesAPI(): boolean {
    return this.cashCountryConfig?.enabledSolServicesAPI ?? false;
  }

  get isEconoCash(): boolean | undefined {
    return (
      this.servicepointSelected?.econoCash &&
      this.countryConfig?.Orders.Pickup.EconoCash !== undefined
    );
  }

  get pickupConfig(): IOrderConfigCommon | undefined {
    return this.countryConfig?.Orders.Pickup;
  }

  get deliveryConfig(): IOrderConfigCommon | undefined {
    return this.countryConfig?.Orders.Delivery;
  }

  get assistanceConfig(): IOrderConfigCommon | undefined {
    return this.countryConfig?.Orders.TechnicalAssistance;
  }

  get isPickup(): boolean {
    return this.requestType?.value === SolCustomerOrderType.PICKUP;
  }

  get isDelivery(): boolean {
    return this.requestType?.value === SolCustomerOrderType.DELIVERY;
  }

  get isDeliveryExchange(): boolean {
    return this.requestType?.value === SolCustomerOrderType.DELIVERY_EXCHANGE;
  }

  get isAssistance(): boolean {
    return this.requestType?.value === SolCustomerOrderType.ASSISTANCE;
  }

  get isSupplyMaterial(): boolean {
    return this.requestType?.value === SolCustomerOrderType.SUPPLY_MATERIAL;
  }

  async initData(): Promise<void> {
    this.requestType = null;
    this.serviceDate = new Date(new Date().setDate(new Date().getDate() + 1));
    this.amount = null;
    this.comments = "";
    this.fault = null;
    this.currency = this.mainCurrency || null;
    this.packQuantities?.forEach((p) => {
      if (p.packQuantity) {
        p.packQuantity = undefined;
      }
    });
    this.denominationQuantities?.forEach((d) => {
      if (d.denominationQuantity) {
        d.denominationQuantity = undefined;
      }
    });
    this.supplies = [];
    this.pickupInitialTime = this.isEconoCash
      ? `${this.pickupConfig?.EconoCash?.DefaultStartTime}:00:00`
      : `${this.pickupConfig?.DefaultStartTime}:00:00`;
    this.pickupFinalTime = this.isEconoCash
      ? `${this.pickupConfig?.EconoCash?.DefaultFinalTime}:00:00`
      : `${this.pickupConfig?.DefaultFinalTime}:00:00`;
    this.deliveryInitialTime = `${this.pickupConfig?.DefaultStartTime}:00:00`;
    this.deliveryFinalTime = `${this.pickupConfig?.DefaultFinalTime}:00:00`;
    this.assistanceInitialTime = `${this.assistanceConfig?.DefaultStartTime}:00:00`;
    this.assistanceFinalTime = `${this.assistanceConfig?.DefaultFinalTime}:00:00`;
    await this.loadOptionsRequestType();
  }

  onChangeRequestType(): void {
    if (this.isDeliveryExchange) {
      this.currency = this.mainCurrency || null;
    }
  }

  async loadOptionsRequestType(): Promise<void> {
    if (this.enabledSolServicesAPI) {
      this.optionsRequestType = (await this.getOptionsRequestTypeBySolService()) || [];
    } else {
      this.optionsRequestType = this.getOptionsRequestTypeBySolCustomer();
    }
  }

  getOptionsRequestTypeBySolCustomer(): { value: SolCustomerOrderType; description: string }[] {
    const options: { value: SolCustomerOrderType; description: string }[] = [];

    if (
      this.$ability.can("manage", "Pickup") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.PICKUP)
    ) {
      options.push({
        value: SolCustomerOrderType.PICKUP,
        description: i18n.t("label.product.pickup-cash").toString(),
      });
    }

    if (
      this.$ability.can("manage", "Delivery") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.DELIVERY)
    ) {
      options.push({
        value: SolCustomerOrderType.DELIVERY,
        description: i18n.t("label.product.delivery-cash").toString(),
      });
    }

    if (
      this.$ability.can("manage", "DeliveryExchange") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.DELIVERY_EXCHHANGE)
    ) {
      options.push({
        value: SolCustomerOrderType.DELIVERY_EXCHANGE,
        description: i18n.t("label.product.delivery-exchange").toString(),
      });
    }

    if (
      this.$ability.can("manage", "AssistanceService") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.ASSISTANCE_SERVICE)
    ) {
      options.push({
        value: SolCustomerOrderType.ASSISTANCE,
        description: i18n.t("label.product.assistance-service").toString(),
      });
    }

    if (
      this.$ability.can("manage", "SupplyMaterialService") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.SUPPLY_MATERIAL_SERVICE)
    ) {
      options.push({
        value: SolCustomerOrderType.SUPPLY_MATERIAL,
        description: i18n.t("label.product.supply-material").toString(),
      });
    }

    return options;
  }

  validateAndResetRequestType(
    options: { value: SolCustomerOrderType; description: string }[]
  ): void {
    if (!options?.find((option) => option.value === this.requestType?.value)) {
      this.requestType = null;
    }
  }

  async getOptionsRequestTypeBySolService(): Promise<
    { value: SolCustomerOrderType; description: string }[]
  > {
    const options: { value: SolCustomerOrderType; description: string }[] = [];

    if (!this.servicepointSelected || !this.getLoggedUser?.country?.code) {
      return options;
    }

    const params: IGetProductsParams = {
      countryCode: this.getLoggedUser.country.code,
      servicePointId: this.servicepointSelected.oidSol || "",
    };

    const response = await this.$services.solServiceApi.getProducts(params);

    if (!response?.products?.length) {
      return options;
    }

    response.products.forEach((product) => {
      if (this.$ability.can("manage", "Pickup") && product.code === MODULE_SOL_SERVICE.PICKUP) {
        options.push({
          value: SolCustomerOrderType.PICKUP,
          description: i18n.t("label.product.pickup-cash").toString(),
        });
      }

      if (this.$ability.can("manage", "Delivery") && product.code === MODULE_SOL_SERVICE.DELIVERY) {
        options.push({
          value: SolCustomerOrderType.DELIVERY,
          description: i18n.t("label.product.delivery-cash").toString(),
        });
      }

      if (
        this.$ability.can("manage", "DeliveryExchange") &&
        product.code === MODULE_SOL_SERVICE.DELIVERY_EXCHHANGE
      ) {
        options.push({
          value: SolCustomerOrderType.DELIVERY_EXCHANGE,
          description: i18n.t("label.product.delivery-exchange").toString(),
        });
      }

      if (
        this.$ability.can("manage", "AssistanceService") &&
        product.code === MODULE_SOL_SERVICE.ASSISTANCE_SERVICE
      ) {
        options.push({
          value: SolCustomerOrderType.ASSISTANCE,
          description: i18n.t("label.product.assistance-service").toString(),
        });
      }

      if (
        this.$ability.can("manage", "SupplyMaterialService") &&
        product.code === MODULE_SOL_SERVICE.SUPPLY_MATERIAL_SERVICE
      ) {
        options.push({
          value: SolCustomerOrderType.SUPPLY_MATERIAL,
          description: i18n.t("label.product.supply-material").toString(),
        });
      }
    });

    return options;
  }

  get mainCurrency(): ISolCustomerCurrency | undefined {
    return this.solCustomerCurrencies?.find((c) => c.mainCurrency === true);
  }

  get _clientSelected(): IUserClientData | undefined {
    return this.clientSelected;
  }
  set _clientSelected(value: IUserClientData | undefined) {
    this.$emit("update:clientSelected", value);
  }
  get _subclientSelected(): IUserClientSubclientData | undefined {
    return this.subclientSelected;
  }
  set _subclientSelected(value: IUserClientSubclientData | undefined) {
    this.$emit("update:subclientSelected", value);
  }
  get _servicepointSelected(): IUserClientSubclientServicepointData | undefined {
    return this.servicepointSelected;
  }
  set _servicepointSelected(value: IUserClientSubclientServicepointData | undefined) {
    this.$emit("update:servicepointSelected", value);
    this.$emit("changeServicePoint", value);
    if (
      this.countryConfig?.Orders.Pickup.EconoCash &&
      this.servicepointSelected?.econoCash !== value?.econoCash
    ) {
      this.pickupInitialTime = value?.econoCash
        ? `${this.pickupConfig?.EconoCash?.DefaultStartTime}:00:00`
        : `${this.pickupConfig?.DefaultStartTime}:00:00`;
      this.pickupFinalTime = value?.econoCash
        ? `${this.pickupConfig?.EconoCash?.DefaultFinalTime}:00:00`
        : `${this.pickupConfig?.DefaultFinalTime}:00:00`;
    }
  }

  @AsyncComputed()
  async faults(): Promise<IFault[] | undefined> {
    if (this.clientSelected) {
      const params = {
        countryCode: this.getLoggedUser?.country?.code,
        entityId: this.clientSelected?.oidSol,
      } as ISolCustomerFaultParams;

      const res = await this.fetchSolCustomerFaults(params);

      return res?.faults || [];
    }
  }

  @Watch("packs")
  onChangePacks(): void {
    this.packQuantities = [];

    this.packs?.forEach((p) => {
      this.packQuantities.push({
        packId: p.id || "",
      });
    });
  }

  @Watch("servicepointSelected", { immediate: true })
  async onChangeServicePoint(): Promise<void> {
    await this.loadOptionsRequestType();
    this.validateAndResetRequestType(this.optionsRequestType);
  }

  @AsyncComputed()
  async denominations(): Promise<ISolCustomerDenomination[] | undefined> {
    if (this.currency) {
      this.denominationQuantities = [];
      const params = {
        countryCode: this.getLoggedUser?.country?.code,
        currencyId: this.currency.currencyId,
      } as ISolCustomerDenominationParams;

      const res = await this.fetchSolCustomerDenominations(params);

      res?.denominations?.forEach((d) => {
        this.denominationQuantities.push({
          denominationId: d.id,
        });
      });

      return res?.denominations || [];
    }
  }

  async isLimitExceeded(): Promise<boolean> {
    const requestTypeFilter =
      this.requestType?.value === SolCustomerOrderType.DELIVERY_EXCHANGE
        ? SolCustomerOrderType.DELIVERY
        : this.requestType?.value;

    const servicepointLimits = await this.$services.servicepoint.getServicepointLimits(
      this.servicepointSelected!.id!,
      requestTypeFilter!
    );
    let exceeded = false;

    servicepointLimits?.limits?.every((limit) => {
      let amount = 0;
      if (this.isPickup) {
        if (this.currency?.isoCode === limit.isoCode) {
          amount = this.amount || 0;
        }
      } else {
        amount = limit.isoCode === this.currency?.isoCode ? this.totalAmountDenominations() : 0;
        amount += this.totalAmountPacks(limit.isoCode);
      }

      if (amount > (limit.amount || 0)) {
        this.$swal({
          title: this.$t("message.newOrder.limit.exceeded.title").toString(),
          text: this.$t("message.newOrder.limit.exceeded.description", [
            this.$options.filters?.currency(
              limit.amount,
              this.solCustomerCurrencies?.find((c) => c.isoCode === limit.isoCode)?.symbol ||
                limit.isoCode
            ),
            "",
          ]).toString(),
          icon: "warning",
          confirmButtonText: this.$t("label.ok").toString(),
        });
        exceeded = true;
      }
      return !exceeded;
    });
    return exceeded;
  }

  async onSubmit(): Promise<void> {
    if (
      (this.isDelivery || this.isDeliveryExchange || this.isPickup) &&
      (await this.isLimitExceeded())
    ) {
      return;
    }

    const { isConfirmed } = await this.modalConfirm();

    if (isConfirmed) {
      let res: ISolCustomerResponse | undefined;

      switch (this.requestType?.value) {
        case SolCustomerOrderType.PICKUP:
          res = await this.submitPickUpForm();
          break;
        case SolCustomerOrderType.DELIVERY:
          res = await this.submitDeliveryForm(false);
          break;
        case SolCustomerOrderType.DELIVERY_EXCHANGE:
          res = await this.submitDeliveryForm(true);
          break;
        case SolCustomerOrderType.ASSISTANCE:
          res = await this.submitAssistanceServiceForm();
          break;
        case SolCustomerOrderType.SUPPLY_MATERIAL:
          res = await this.submitSupplyForm();
          break;
      }

      if (res) {
        const submissionResult = extractSolSubmissionResult(res);
        const isSuccess = isSolSubmissionSuccess(submissionResult);
        const toastVariant = isSuccess ? "success" : "danger";
        const toastMessage = resolveSolSubmissionMessage({
          result: submissionResult,
          te: (key) => (typeof this.$te === "function" ? this.$te(key) : false),
          t: (key) => this.$t(key).toString(),
          successFallbackKey: "message.order.send.success",
          failureFallbackKey: "message.order.send.failed.create",
        });

        this.makeToast(toastVariant, toastMessage);
        this.$emit("success");
        this.hideModal();
      } else {
        this.makeToast("danger", i18n.t("message.order.send.failed.create").toString());
        this.$emit("success");
        this.hideModal();
      }
    }
  }

  async submitPickUpForm(): Promise<ISolCustomerResponse | undefined> {
    const pickUpService = {
      countryCode: this.getLoggedUser.country?.code,
      comments: this.comments || undefined,
      servicePointId: this.servicepointSelected?.oidSol,
      serviceDate: format(this.serviceDate, this.datePattern),
      isChange: false,
      origin: this.origin,
      hasDate: true,
      hasLimitDate: false,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
    } as IPickUpService;

    if (!this.servicepointSelected?.agilePickup && this.amount) {
      pickUpService.requestedAmounts = [
        {
          currencyId: this.currency?.currencyId,
          totalAmount: Number(this.amount),
        } as IRequestedAmount,
      ];
    }

    if (this.pickupConfig?.AllowIntervalTime) {
      const requestInitialTime = new Date(this.serviceDate);
      const requestFinalTime = new Date(this.serviceDate);
      const initialHour = this.pickupInitialTime.split(":")[0];
      const initialMinute = this.pickupInitialTime.split(":")[1];
      const finalHour = this.pickupFinalTime.split(":")[0];
      const finalMinute = this.pickupFinalTime.split(":")[1];

      requestInitialTime.setHours(Number(initialHour));
      requestInitialTime.setMinutes(Number(initialMinute));
      requestInitialTime.setSeconds(0);

      requestFinalTime.setHours(Number(finalHour));
      requestFinalTime.setMinutes(Number(finalMinute));
      requestFinalTime.setSeconds(0);

      pickUpService.requestInitialTime = format(requestInitialTime, this.dateTimePattern);
      pickUpService.requestFinalTime = format(requestFinalTime, this.dateTimePattern);
    }

    return this.savePickUpService(new PickUpService(pickUpService));
  }

  async submitDeliveryForm(isChange: boolean): Promise<ISolCustomerResponse | undefined> {
    const deliveryService = {
      countryCode: this.getLoggedUser.country?.code,
      comments: this.comments || undefined,
      servicePointId: this.servicepointSelected?.oidSol,
      serviceDate: format(this.serviceDate, this.datePattern),
      isChange: isChange,
      origin: this.origin,
      hasDate: true,
      hasLimitDate: false,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
      requestedAmounts: [],
    } as IDeliveryService;

    const denominations = this.denominations as any as ISolCustomerDenomination[] | undefined;
    if (denominations?.length) {
      const denominationsWithQuantity: ISolCustomerDenominationQuantity[] =
        this.getDenominationsWithQuantity(this.denominationQuantities);
      if (denominationsWithQuantity.length > 0) {
        const requestedAmount: IRequestedAmount = {
          currencyId: this.currency?.currencyId,
          denominations: denominationsWithQuantity,
          packs: [],
          totalAmount: this.totalAmountDenominations(),
        };
        deliveryService.requestedAmounts?.push(requestedAmount);
      }
    }

    const packs = this.packs as any as ISolCustomerPack[];

    for (const packWithQuantity of this.getPacksWithQuantity(this.packQuantities)) {
      const pack = packs.find((p) => p.id === packWithQuantity.packId);
      const denominationsPack = pack?.denominations || [];

      const currency = this.solCustomerCurrencies?.find(
        (c) => c.isoCode === denominationsPack[0].currencyIsoCode
      );

      const requestedAmount = deliveryService.requestedAmounts?.find(
        (r) => r.currencyId === currency?.currencyId
      );

      if (requestedAmount) {
        requestedAmount.packs?.push(packWithQuantity);
      } else {
        deliveryService.requestedAmounts?.push({
          currencyId: currency?.currencyId,
          denominations: [],
          packs: [packWithQuantity],
          totalAmount: 0,
        } as IRequestedAmount);
      }
    }

    if (this.deliveryConfig?.AllowIntervalTime) {
      const requestInitialTime = new Date(this.serviceDate);
      const requestFinalTime = new Date(this.serviceDate);
      const initialHour = this.deliveryInitialTime.split(":")[0];
      const initialMinute = this.deliveryInitialTime.split(":")[1];
      const finalHour = this.deliveryFinalTime.split(":")[0];
      const finalMinute = this.deliveryFinalTime.split(":")[1];

      requestInitialTime.setHours(Number(initialHour));
      requestInitialTime.setMinutes(Number(initialMinute));
      requestInitialTime.setSeconds(0);

      requestFinalTime.setHours(Number(finalHour));
      requestFinalTime.setMinutes(Number(finalMinute));
      requestFinalTime.setSeconds(0);

      deliveryService.requestInitialTime = format(requestInitialTime, this.dateTimePattern);
      deliveryService.requestFinalTime = format(requestFinalTime, this.dateTimePattern);
    }

    return this.saveDeliveryService(deliveryService);
  }

  async submitAssistanceServiceForm(): Promise<ISolCustomerResponse | undefined> {
    const assistanceService = {
      countryCode: this.getLoggedUser.country?.code,
      assistanceObservation: this.comments || undefined,
      servicePointId: this.servicepointSelected?.oidSol,
      serviceDate: format(this.serviceDate, this.datePattern),
      origin: this.origin,
      hasDate: true,
      hasLimitDate: false,
      faultDescription: this.fault?.faultDescription,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
    } as IAssistanceService;

    if (this.assistanceConfig?.AllowIntervalTime) {
      const requestInitialTime = new Date(this.serviceDate);
      const requestFinalTime = new Date(this.serviceDate);
      const initialHour = this.assistanceInitialTime.split(":")[0];
      const initialMinute = this.assistanceInitialTime.split(":")[1];
      const finalHour = this.assistanceFinalTime.split(":")[0];
      const finalMinute = this.assistanceFinalTime.split(":")[1];

      requestInitialTime.setHours(Number(initialHour));
      requestInitialTime.setMinutes(Number(initialMinute));
      requestInitialTime.setSeconds(0);

      requestFinalTime.setHours(Number(finalHour));
      requestFinalTime.setMinutes(Number(finalMinute));
      requestFinalTime.setSeconds(0);

      assistanceService.requestInitialTime = format(requestInitialTime, this.dateTimePattern);
      assistanceService.requestFinalTime = format(requestFinalTime, this.dateTimePattern);
    }

    return this.saveAssistanceService(assistanceService);
  }

  async submitSupplyForm(): Promise<ISolCustomerResponse | undefined> {
    const supplyMaterialService = {
      countryCode: this.getLoggedUser.country?.code,
      servicePointId: this.servicepointSelected?.oidSol,
      origin: this.origin,
      hasDate: false,
      hasLimitDate: false,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
      supplies: this.supplies,
    } as ISolCustomerSupplyMaterialService;

    return this.saveSupplyMaterialService(supplyMaterialService);
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.servicepointSelected?.flowApproval
        ? i18n.t("message.newOrder.confirm.approval.title").toString()
        : i18n.t("message.newOrder.confirm.title").toString(),
      html: this.servicepointSelected?.flowApproval
        ? i18n.t("message.newOrder.confirm.approval.description").toString()
        : i18n.t("message.newOrder.confirm.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  hideModal(): void {
    const pModal = this.$refs.newOrderModal as PModal;
    const bModal = pModal.$children[0] as BModal;
    bModal.hide();
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  getDenominationsWithQuantity(
    denominations: ISolCustomerDenominationQuantity[]
  ): ISolCustomerDenominationQuantity[] {
    const denominationsWithQuantity: ISolCustomerDenominationQuantity[] = [];

    denominations.forEach((d) => (d.denominationQuantity ? denominationsWithQuantity.push(d) : ""));

    return denominationsWithQuantity;
  }

  getPacksWithQuantity(packs: ISolCustomerPackQuantity[]): ISolCustomerPackQuantity[] {
    const packsWithQuantity: ISolCustomerPackQuantity[] = [];

    packs.forEach((p) => (p.packQuantity ? packsWithQuantity.push(p) : ""));

    return packsWithQuantity;
  }

  totalAmountDenominations(): number {
    let totalAmount = 0;
    const denominations = this.denominations as any as ISolCustomerDenomination[];

    if (denominations) {
      denominations.forEach((denomination, i) => {
        if (
          denomination?.value &&
          this.denominationQuantities &&
          this.denominationQuantities[i]?.denominationQuantity
        ) {
          totalAmount +=
            denomination?.value * (this.denominationQuantities[i].denominationQuantity || 0);
        }
      });
    }

    return totalAmount;
  }

  totalAmountPacks(isoCode?: string): number {
    let totalAmount = 0;
    const packs = this.packs as any as ISolCustomerPack[];

    if (packs) {
      packs.forEach((pack, index) => {
        if (this.packQuantities && this.packQuantities[index]) {
          const denominations = isoCode
            ? pack.denominations?.filter(
                (d: ISolCustomerDenominationPack) => d.currencyIsoCode === isoCode
              )
            : pack.denominations;

          const packQuantity = this.packQuantities[index].packQuantity || 0;

          totalAmount += packQuantity * this.sumDenominations(denominations);
        }
      });
    }

    return totalAmount;
  }
  totalAmount(): number {
    return this.totalAmountDenominations() + this.totalAmountPacks();
  }

  sumDenominations(denominations: ISolCustomerDenominationPack[] | undefined): number {
    let amount = 0;

    denominations?.forEach(
      (denomination) => (amount += (denomination?.quantity || 0) * (denomination?.value || 0))
    );

    return amount;
  }
}
